<template>
  <div
    :class="sizeClass + ' mx-auto h-full'"
    :style="ratio ? {aspectRatio: ratio} : {}"
  >
    <div
      v-if="loading"
      :class="sizeClass + ' h-full flex items-center justify-center'"
    >
      <BaseSpinner v-if="loaderType === 'spinner'" :size="spinnerSize" />
      <div v-else class="w-full h-full bg-gray-200 animate-pulse rounded">
        <FileImage class="w-full h-full text-gray-500 stroke-1" />
      </div>
    </div>
    <div
      v-else
      class="grid justify-items-center"
    >
      <div
        v-if="imageUrl"
        class="relative"
      >
        <img
          :src="(imageUrl as string | undefined)"
          alt="ordonnance"
          :class="sizeClass + ' cursor-pointer object-contain'"
          @click="!preventClick && download()"
        >
        <div
          v-if="printButton"
          class="absolute bottom-8 right-8"
        >
          <BaseButton
            primary
            class="rounded-3xl"
            style="padding:10px !important"
            @click="download()"
          >
            <v-icon color="white">
              mdi-printer
            </v-icon>
          </BaseButton>
        </div>
      </div>
      <Prescription
        v-else
        class="max-h-28 w-full"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, ref} from "vue";
import FileUrl from "@/core/interfaces/FileUrl";
import Prescription from "@/core/components/icons/Prescription.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import BaseSpinner from "@/core/components/base/spinner/BaseSpinner.vue";
import {secureFileApi} from "@/container";
import {FileImage} from "@lucide/vue";
import fileHelper from "@/core/helpers/fileHelper";

interface Props {
    urls: FileUrl[];
    size?: number;
    printButton?: boolean;
    ratio?: number;
    preventClick?: boolean;
    loaderType?: 'spinner' | 'skeleton';
}

const {
    urls,
    size = 84,
    ratio = null,
    printButton = false,
    preventClick = false,
    loaderType = 'spinner'
} = defineProps<Props>();

const emit = defineEmits<{
    (e: 'loaded', blob: Blob): void;
    (e: 'error'): void;
}>();

const imageUrl = ref<string | ArrayBuffer | null>(null);
const images = ref<(string | ArrayBuffer | null)[]>([]);
const pdfUrl = ref<string | null>(null);
const loading = ref(true);

const sizeClass = computed(() => `w-${size}`);

const spinnerSize = computed(() => size >= 84 ? 'medium' : 'medium-small');

onMounted(() => {
    if (urls.length) {
        const url = urls[0].thumbnail.large;
        pdfUrl.value = urls[0].pdf || null;
        getImage(url);

        if (urls.length > 1) {
            getImages(urls);
        }
    }
});

const getImage = (url: string) => {
    secureFileApi().get(url)
        .then((blob: Blob) => {
            const reader = new FileReader();

            reader.readAsDataURL(blob);

            reader.onload = () => {
                imageUrl.value = reader.result;
                emit('loaded', blob);
            };
        })
        .catch(() => {
            emit('error');
        })
        .finally(() => {
            loading.value = false;
        });
};

const getImages = (urls: FileUrl[]) => {
    urls.map(url => {
        secureFileApi()
            .get(url.thumbnail.large)
            .then(response => {
                const reader = new FileReader();

                reader.readAsDataURL(response);

                reader.onload = () => {
                    images.value.push(reader.result);
                };
            });
    });
};

const download = () => {
    if (pdfUrl.value) {
        fileHelper.printPdf(pdfUrl.value);
    } else {
        printImages();
    }
};

const printImages = () => {
    const printWindow = window.open(
        '',
        'modal',
        'width=' + screen.availWidth + ',height=' + screen.availHeight
    );

    if (printWindow) {
        printWindow.document.write('<html lang="fr"><head><title>Visuel</title></head>');
        printWindow.document.write('<body>');

        if (images.value.length) {
            images.value.map(image => {
                printWindow.document.write('<img src="' + image + '" alt="Ordonnance"/>');
            });
        } else {
            printWindow.document.write('<img src="' + imageUrl.value + '" alt="Ordonnance"/>');
        }

        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();
    }
};
</script>
